import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import get from 'lodash/get';

import { uploadAndCreateImage } from '@src/services/api/images';
import { formatBytes } from '@src/utils/number-helpers';
import Dropzone from './image_uploader_dropzone';
import Actions from './image_uploader_actions';

const ImageUploader = (props) => {
    const { folderName, onCancel, onImageUploaded } = props;

    const [imageBlob, setImageBlob] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [error, setError] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    const handleFileSelection = useCallback((imageFile) => {
        setImageFile(imageFile);

        const reader = new FileReader();
        reader.onabort = () => {
            setImageBlob(null);
            setError('file reading was aborted');
        };

        reader.onerror = () => {
            setImageBlob(null);
            setError('file reading has failed');
        };

        reader.onload = () => {
            setImageBlob(reader.result);
            setError(null);
        };

        reader.readAsDataURL(imageFile);
    }, []);

    const handleImageUploadError = useCallback((error) => {
        let errorMessage = error.messsage;

        const responseMessage = get(error, ['response', 'data', 'error', 'message']);
        if (responseMessage) {
            errorMessage = responseMessage.replace(
                /(\d+(?:((?=\.))\d*))/gm,
                (match) => formatBytes(match),
            );
        }

        setIsUploading(false);
        setImageBlob(null);
        setError(errorMessage);
    }, [setIsUploading, setImageBlob, setError]);

    const handleImageUploadSuccess = useCallback((image) => {
        setIsUploading(false);
        setError(null);

        onImageUploaded(image);
    }, [setIsUploading, setError, onImageUploaded]);

    const handleImageSave = useCallback((file) => {
        setIsUploading(true);
        uploadAndCreateImage(file, folderName)
            .then((image) => {
                handleImageUploadSuccess(image);
            }).catch((error) => {
                handleImageUploadError(error);
            });
    }, [folderName, setIsUploading, handleImageUploadSuccess, handleImageUploadError]);

    const handleSaveClick = useCallback((e) => {
        e.preventDefault();

        if (imageFile) {
            handleImageSave(imageFile);
        } else {
            // eslint-disable-next-line no-alert
            alert('No Image Selected');
        }
    }, [imageFile, handleImageSave]);

    return (
        <Card>
            { imageBlob ? null : <Dropzone onFileSelected={handleFileSelection} /> }
            <Grid container sx={{ width: '100%' }} className="" justifyContent="center" alignItems="center">
                <Grid size={6}>
                    { imageBlob ?
                        <Paper>
                            <img
                                style={{
                                    backgroundSize: 'contain',
                                    objectFit: 'cover',
                                    width: '100%',
                                }}
                                src={imageBlob}
                                alt="Selected image"
                            />
                        </Paper> :
                        null}
                    { error ? <Typography variation="caption" align="center" color="error">{error}</Typography> : null }
                </Grid>
            </Grid>
            <CardActions>
                <Actions
                    isFileSelected={Boolean(imageBlob)}
                    isUploading={isUploading}
                    onSave={handleSaveClick}
                    onCancel={onCancel}
                />
            </CardActions>
        </Card>
    );
};

ImageUploader.propTypes = {
    folderName: PropTypes.oneOf(['featured', 'thumbnail', 'content']).isRequired,
    onCancel: PropTypes.func.isRequired,
    onImageUploaded: PropTypes.func,
};

export default React.memo(ImageUploader);
